<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<script lang="ts">
  import Panel from '@components/panel.svelte'
  import EntityLink from '@components/entity-link.svelte'
  import Image from '@components/image.svelte'
  import {
    getDisplay,
    getValue,
    formatNhlPosition,
    type GameraTeamReference,
    type NhlStatKey,
    type StatModel,
  } from '@statmuse/core/gamera'
  import { playerStats, players } from './stores'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined

  type Selection = 'forward' | 'defender' | 'goalie'

  const matchups = {
    forward: [
      {
        label: 'Goals',
        statkey: 'Goals',
        compare: (a, b) =>
          getValue(a ?? {}, 'Goals') > getValue(b ?? {}, 'Goals'),
      },
      {
        label: 'Points',
        statkey: 'Points',
        compare: (a, b) =>
          getValue(a ?? {}, 'Points') > getValue(b ?? {}, 'Points'),
      },
      {
        label: 'Shots on Goal',
        statkey: 'Shots',
        compare: (a, b) =>
          getValue(a ?? {}, 'Shots') > getValue(b ?? {}, 'Shots'),
      },
    ],
    defender: [
      {
        label: 'Points',
        statkey: 'Points',
        compare: (a, b) =>
          getValue(a ?? {}, 'Points') > getValue(b ?? {}, 'Points'),
      },
      {
        label: 'Blocks',
        statkey: 'BlockedShots',
        compare: (a, b) =>
          getValue(a ?? {}, 'BlockedShots') > getValue(b ?? {}, 'BlockedShots'),
      },
      {
        label: 'Hits',
        statkey: 'Hits',
        compare: (a, b) =>
          getValue(a ?? {}, 'Hits') > getValue(b ?? {}, 'Hits'),
      },
    ],
    goalie: [
      {
        label: 'Shots Faced',
        statkey: 'ShotsAgainst',
        compare: (a, b) =>
          getValue(a ?? {}, 'ShotsAgainst') > getValue(b ?? {}, 'ShotsAgainst'),
      },
      {
        label: 'Saves',
        statkey: 'Saves',
        compare: (a, b) =>
          getValue(a ?? {}, 'Saves') > getValue(b ?? {}, 'Saves'),
      },
      {
        label: 'Save Pct',
        statkey: 'SavePercentage',
        compare: (a, b) =>
          getValue(a ?? {}, 'SavePercentage') >
          getValue(b ?? {}, 'SavePercentage'),
      },
    ],
  } as Record<
    Selection,
    {
      label: string
      statkey: NhlStatKey
      compare: (a?: StatModel<NhlStatKey>, b?: StatModel<NhlStatKey>) => boolean
    }[]
  >

  let selected: Selection = 'forward'

  $: defenderIds = Object.entries($players)
    .filter((x) => x[1]?.position === 'DEFENSEMAN')
    .map((x) => x[1]?.id)
  $: goalieIds = Object.entries($players)
    .filter((x) => x[1]?.position === 'GOALIE')
    .map((x) => x[1]?.id)
  $: forwardIds = Object.entries($players)
    .filter(
      (x) => x[1]?.position !== 'DEFENSEMAN' && x[1]?.position !== 'GOALIE',
    )
    .map((x) => x[1]?.id)

  $: selectedMatchup = matchups[selected]

  $: awayStats = [
    ...Object.entries($playerStats.away?.statsLookup ?? {}).filter((x) =>
      x[0].includes('p-'),
    ),
  ]

  $: homeStats = [
    ...Object.entries($playerStats.home?.statsLookup ?? {}).filter((x) =>
      x[0].includes('p-'),
    ),
  ]

  $: awayForward = awayStats
    .filter((x) => forwardIds.includes(x[0].split('p-')[1]))
    .filter(
      (s) =>
        getValue(s[1] ?? {}, 'Points') > 0 || getValue(s[1] ?? {}, 'Shots'),
    )
    .sort((a, b) => {
      if (getValue(b[1] ?? {}, 'Points') !== getValue(a[1] ?? {}, 'Points'))
        return getValue(b[1] ?? {}, 'Points') - getValue(a[1] ?? {}, 'Points')
      return getValue(b[1] ?? {}, 'Shots') - getValue(a[1] ?? {}, 'Shots')
    })[0] as [string, StatModel<NhlStatKey>] | undefined

  $: homeForward = homeStats
    .filter((x) => forwardIds.includes(x[0].split('p-')[1]))
    .filter(
      (s) =>
        getValue(s[1] ?? {}, 'Points') > 0 || getValue(s[1] ?? {}, 'Shots'),
    )
    .sort((a, b) => {
      if (getValue(b[1] ?? {}, 'Points') !== getValue(a[1] ?? {}, 'Points'))
        return getValue(b[1] ?? {}, 'Points') - getValue(a[1] ?? {}, 'Points')
      return getValue(b[1] ?? {}, 'Shots') - getValue(a[1] ?? {}, 'Shots')
    })[0] as [string, StatModel<NhlStatKey>] | undefined

  $: awayDefender = awayStats
    .filter((x) => defenderIds.includes(x[0].split('p-')[1]))
    .filter(
      (s) =>
        getValue(s[1] ?? {}, 'Points') > 0 ||
        getValue(s[1] ?? {}, 'BlockedShots') > 0 ||
        getValue(s[1] ?? {}, 'Hits') > 0,
    )
    .sort((a, b) => {
      if (getValue(b[1] ?? {}, 'Points') !== getValue(a[1] ?? {}, 'Points'))
        return getValue(b[1] ?? {}, 'Points') - getValue(a[1] ?? {}, 'Points')
      if (
        getValue(b[1] ?? {}, 'BlockedShots') !==
        getValue(a[1] ?? {}, 'BlockedShots')
      )
        return (
          getValue(b[1] ?? {}, 'BlockedShots') -
          getValue(a[1] ?? {}, 'BlockedShots')
        )
      return getValue(b[1] ?? {}, 'Hits') - getValue(a[1] ?? {}, 'Hits')
    })[0] as [string, StatModel<NhlStatKey>] | undefined

  $: homeDefender = homeStats
    .filter((x) => defenderIds.includes(x[0].split('p-')[1]))
    .filter(
      (s) =>
        getValue(s[1] ?? {}, 'Points') > 0 ||
        getValue(s[1] ?? {}, 'BlockedShots') > 0 ||
        getValue(s[1] ?? {}, 'Hits') > 0,
    )
    .sort((a, b) => {
      if (getValue(b[1] ?? {}, 'Points') !== getValue(a[1] ?? {}, 'Points'))
        return getValue(b[1] ?? {}, 'Points') - getValue(a[1] ?? {}, 'Points')
      if (
        getValue(b[1] ?? {}, 'BlockedShots') !==
        getValue(a[1] ?? {}, 'BlockedShots')
      )
        return (
          getValue(b[1] ?? {}, 'BlockedShots') -
          getValue(a[1] ?? {}, 'BlockedShots')
        )
      return getValue(b[1] ?? {}, 'Hits') - getValue(a[1] ?? {}, 'Hits')
    })[0] as [string, StatModel<NhlStatKey>] | undefined

  $: awayGoalie = awayStats
    .filter((x) => goalieIds.includes(x[0].split('p-')[1]))
    .filter(
      (s) =>
        getValue(s[1] ?? {}, 'Saves') > 0 ||
        getValue(s[1] ?? {}, 'ShotsAgainst') > 0,
    )
    .sort((a, b) => {
      if (getValue(b[1] ?? {}, 'Saves') !== getValue(a[1] ?? {}, 'Saves'))
        return getValue(b[1] ?? {}, 'Saves') - getValue(a[1] ?? {}, 'Saves')
      return (
        getValue(a[1] ?? {}, 'ShotsAgainst') -
        getValue(b[1] ?? {}, 'ShotsAgainst')
      )
    })[0] as [string, StatModel<NhlStatKey>] | undefined

  $: homeGoalie = homeStats
    .filter((x) => goalieIds.includes(x[0].split('p-')[1]))
    .filter(
      (s) =>
        getValue(s[1] ?? {}, 'Saves') > 0 ||
        getValue(s[1] ?? {}, 'ShotsAgainst') > 0,
    )
    .sort((a, b) => {
      if (getValue(b[1] ?? {}, 'Saves') !== getValue(a[1] ?? {}, 'Saves'))
        return getValue(b[1] ?? {}, 'Saves') - getValue(a[1] ?? {}, 'Saves')
      return (
        getValue(a[1] ?? {}, 'ShotsAgainst') -
        getValue(b[1] ?? {}, 'ShotsAgainst')
      )
    })[0] as [string, StatModel<NhlStatKey>] | undefined

  $: selectedPlayerStats =
    selected === 'forward'
      ? [awayForward, homeForward]
      : selected === 'defender'
      ? [awayDefender, homeDefender]
      : [awayGoalie, homeGoalie]

  $: awaySelectedPlayer =
    $players[selectedPlayerStats[0]?.[0].replace('p-', '').split(':')[2] ?? 0]
  $: homeSelectedPlayer =
    $players[selectedPlayerStats[1]?.[0].replace('p-', '').split(':')[2] ?? 0]
</script>

{#if awayStats.length > 0 || homeStats.length > 0}
  <Panel class="!p-0">
    <div class="flex justify-between items-center px-3 py-2">
      <h3 class="font-semibold">Matchup</h3>
      <div
        class="bg-gray-6 dark:bg-gray-4 text-gray-5 border border-gray-6 dark:border-gray-3 flex text-center rounded-full relative overflow-clip"
      >
        <span
          class="absolute left-0 h-full w-1/3 bg-gray-8 dark:bg-gray-7 rounded-full transition-transform ease-out"
          class:translate-x-0={selected === 'forward'}
          class:translate-x-full={selected === 'defender'}
          class:translate-x-24={selected === 'goalie'}
        />
        <span
          class="py-1 relative cursor-pointer w-12"
          class:text-gray-2={selected === 'forward'}
          on:click={() => (selected = 'forward')}
        >
          F
        </span>
        <span
          class="py-1 relative cursor-pointer w-12"
          class:text-gray-2={selected === 'defender'}
          on:click={() => (selected = 'defender')}
        >
          D
        </span>
        <span
          class="py-1 relative cursor-pointer w-12"
          class:text-gray-2={selected === 'goalie'}
          on:click={() => (selected = 'goalie')}
        >
          G
        </span>
      </div>
    </div>
    <div
      class="flex justify-between px-3 border-b border-gray-6 dark:border-gray-4"
    >
      <EntityLink
        entity={awaySelectedPlayer && {
          type: 'player',
          id: awaySelectedPlayer.id,
          display: awaySelectedPlayer.longName,
          domain: 'NHL',
        }}
      >
        <Image
          src={awaySelectedPlayer?.media.bustOrSilhouette?.imageUrl ?? ''}
          alt={awaySelectedPlayer?.longName ?? ''}
          width={120}
          height={90}
          class="h-[90px] object-contain object-left"
        />
      </EntityLink>
      <EntityLink
        entity={homeSelectedPlayer && {
          type: 'player',
          id: homeSelectedPlayer.id,
          display: homeSelectedPlayer.longName,
          domain: 'NHL',
        }}
      >
        <Image
          src={homeSelectedPlayer?.media.bustOrSilhouette?.imageUrl ?? ''}
          alt={homeSelectedPlayer?.longName ?? ''}
          width={120}
          height={90}
          class="h-[90px] object-contain object-right"
        />
      </EntityLink>
    </div>

    <div class="divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1">
      <div class="flex justify-between !px-3">
        <div>
          {#if awaySelectedPlayer}
            <EntityLink
              entity={awaySelectedPlayer && {
                type: 'player',
                id: awaySelectedPlayer.id,
                display: awaySelectedPlayer.longName,
                domain: 'NHL',
              }}
              class="font-semibold text-inherit"
            >
              {awaySelectedPlayer.longName}
            </EntityLink>

            <p class="text-[16px] text-gray-5">
              {awayTeam?.abbreviation}
              {formatNhlPosition(awaySelectedPlayer.position)} #{awaySelectedPlayer.uniformNumber}
            </p>
          {/if}
        </div>
        <div class="text-right">
          {#if homeSelectedPlayer}
            <EntityLink
              entity={homeSelectedPlayer && {
                type: 'player',
                id: homeSelectedPlayer.id,
                display: homeSelectedPlayer.longName,
                domain: 'NHL',
              }}
              class="font-semibold text-inherit"
            >
              {homeSelectedPlayer.longName}
            </EntityLink>

            <p class="text-[16px] text-gray-5">
              {homeTeam?.abbreviation}
              {formatNhlPosition(homeSelectedPlayer.position)} #{homeSelectedPlayer.uniformNumber}
            </p>
          {/if}
        </div>
      </div>
      {#each selectedMatchup as matchup (matchup.label)}
        <div class="flex justify-center relative">
          <p
            class="absolute top-1 left-1 px-2 rounded-2xl"
            style={matchup.compare(
              selectedPlayerStats[0]?.[1],
              selectedPlayerStats[1]?.[1],
            )
              ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
              : undefined}
          >
            {getDisplay(
              selectedPlayerStats[0] ? selectedPlayerStats[0][1] : {},
              matchup.statkey,
            )}
          </p>
          <p class="text-gray-5">{matchup.label}</p>
          <p
            class="absolute top-1 right-1 px-2 rounded-2xl"
            style={matchup.compare(
              selectedPlayerStats[1]?.[1],
              selectedPlayerStats[0]?.[1],
            )
              ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
              : undefined}
          >
            {getDisplay(
              selectedPlayerStats[1] ? selectedPlayerStats[1][1] : {},
              matchup.statkey,
            )}
          </p>
        </div>
      {/each}
    </div>
  </Panel>
{/if}
